import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { NamespaceConsumer, NamespaceShape, withNamespace } from '@ux/namespace-component';
import { IntlProvider, FormattedMessage } from 'react-intl';
import Cart from '@px/header-cart';
import AccountDelegation from '@ux/account-delegation';
import AccountTray from './account-tray/account-tray';
import AccountTrayComponent from '@ux/account-tray';
import MarketSelector from '@ux/market-selector';
import ContactTray from './contact-tray';
import ProductPanel from '@ux/product-panel';
import HeaderNavigation from '@ux/header-navigation';
import Logo from '@ux/logo';
import Button from '@ux/button';
import MagnifyingGlass from '@ux/icon/magnifying-glass';
import Help from '@ux/icon/help';
import assign from 'object-assign';
import Link from '@ux/link';
import { golf, defaultComponents } from './enums';
import eidBuilder from './eid';
import golfToEid from 'golf-to-eid';
import LinkBuilder from './link-builder.js';
const Notifications = lazy(() => import(/* webpackChunkName: "notifications" */ '@ux/notifications'));

/**
 * Render and expose the Utility as a functional Component.
 *
 * @param {Object} props Properties
 * @returns {ReactElement} Namespaced UtilityBar.
 * @api public
 */
class UtilityBar extends NamespaceConsumer {
  constructor(props) {
    super(...arguments);

    //
    // Merge `components` from props with the default components so that incoming
    // props only have to set things that should be different than the default.
    //
    this.components = { ...defaultComponents, ...props.components };
    this.oldBrand = props.isReseller &&
      props.preset !== 'brand-header' &&
      props.preset !== 'language-header';
    this.eids = {
      blogLink: eidBuilder({ key: golf.blogLink, manifest: props.manifest })
    };
  }

  /**
   * Update this.components when props.components changes
   *
   * @param {Object} nextProps the new properties that are received
   * @api private
   */
  componentWillReceiveProps(nextProps) {
    // Merge `components` from props with the default components so that incoming props only have to set things that should be
    // different than the default.
    this.components = { ...defaultComponents, ...nextProps.components };
  }

  /**
   * Remove the eventListeners of breakpoints.
   *
   * @api private
   */
  componentWillUnmount() {
    this.breakup();
  }

  /**
   * Add eventListeners for breakpoints. Notify listeners the utilityBar
   * is mounted and expose the instance.
   *
   * @api private
   */
  componentDidMount() {
    this.breakpoint('mobile', 'phablet', 'tablet', 'desktop', 'large');
  }

  /**
   * Re-scale the headerNav after username and cart data are available
   *
   * @param {Object} prevProps The previous props for utility-bar
   * @api private
   */
  componentDidUpdate(prevProps) {
    const { items, cart = {}, customer } = this.props;
    if (this.headerNav && (
      items !== prevProps.items ||
      (cart.items !== (prevProps.cart && prevProps.cart.items)) ||
      customer.shopperId !== prevProps.shopperId)) {
      this.headerNav.measureContainer();
      this.headerNav.scale(true);
    }
  }

  /**
   * Prefer state over props when assigning to children
   *
   * @returns {Object} Child props
   * @api private
   */
  getChildProps() {
    return assign({}, this.props, this.state);
  }

  /**
   * Render notifications module
   *
   * @returns {React.ReactElement} Notifications component
   */
  renderNotifications() {
    const props = this.props;
    const { urls, messages, privateLabelId } = props;
    const headerText = messages[golf.notificationsHeaderText];
    const notificationProps = {
      showCount: false,
      showOpenCaret: true,
      manifest: props.manifest,
      sso: {
        href: urls.sso.href,
        login: urls.sso.login
      },
      market: props.market,
      shopperId: props.shopperId,
      privateLabelId,
      messages: {
        noNotificationsTitle: messages[golf.noNotificationsTitle],
        noNotificationsText: messages[golf.noNotificationsText],
        failedNotificationsTitle: messages[golf.failedNotificationsTitle],
        failedNotificationsText: messages[golf.failedNotificationsText],
        failedNotificationsLink: messages[golf.failedNotificationsLink],
        headerText
      },
      tooltip: headerText,
      ...urls.notifications
    };

    return (typeof window !== 'undefined') && (
      <Suspense fallback={ <div className={ this.namespace('notifications-fallback') }/> }>
        <Notifications { ...notificationProps } />
      </Suspense>
    );
  }

  /**
   * Renders help, notifications, account tray, cart in that order
   * @param  {Object} cartProps  Props specific to cart
   * @param  {Object} childProps Values assembled from combining props and state
   * @returns {React.ReactElement} A right-aligned div containing global elements
   */
  renderGlobalElements(cartProps, childProps) {
    const props = this.props;
    const components = this.components;
    const { CartComponent, showAccountTrayComponent, messages, urlArgs, urls } = props;
    const webMailLinks = [
      {
        'href': urls.sso.o365Login.href,
        'id': golf.o365LoginEXP,
        'data-eid': golfToEid(golf.o365LoginEXP, {
          prefix: 'uxp.hydra.utility_bar_account_tray',
          postfix: 'link.click'
        }),
        'urlArgs': urlArgs,
        'children': <FormattedMessage id={ golf.o365LoginEXP } />
      },
      {
        'href': urls.webmailLogin.href,
        'id': golf.webmailLoginEXP,
        'data-eid': golfToEid(golf.webmailLoginEXP, {
          prefix: 'uxp.hydra.utility_bar_account_tray',
          postfix: 'link.click'
        }),
        'urlArgs': urlArgs,
        'children': <FormattedMessage id={ golf.webmailLoginEXP } />
      }
    ];

    return (
      <React.Fragment>
        {
          components.help && this.renderHelpCenter()
        }
        {
          components.notifications && props.loggedIn &&
          this.renderNotifications()
        }
        {
          components.accountTray && (showAccountTrayComponent ? <AccountTrayComponent bottomLinks={ webMailLinks } { ...childProps } messages={ messages } /> : <AccountTray { ...childProps } />)
        }
        {
          components.cart && (CartComponent ? <CartComponent { ...cartProps }/> : <Cart { ...cartProps } />)
        }
      </React.Fragment>
    );
  }

  renderDomainSearch() {
    const mobileSearch = <Link urlArgs={ this.props.urlArgs }
      { ...this.props.urls.domainSearch }
      className={ this.namespace('mobile-header-domain-search') } />;

    const action = `${this.props.urls.home.href}/domainsearch/find`;
    return (
      <React.Fragment>
        { mobileSearch }
        <FormattedMessage id='Sales:DomainSearch:Input:Placeholder'>
          {(placeholder) =>
            <form method='GET' action={ action } className={ this.namespace('input-group', 'header-domain-search') }>
              <input placeholder={ placeholder }
                type='text'
                name='domainToCheck'
                className={ this.namespace('form-control') }/>
              <span className={ this.namespace('input-group-btn') }>
                <Button design='addon' type='submit' value='Submit'>
                  <MagnifyingGlass />
                </Button>
              </span>
            </form>
          }
        </FormattedMessage>
      </React.Fragment>
    );
  }

  renderHelpCenter() {
    const { fullHelpLinkId, urlArgs, urls, split, supportContacts:
      { technicalSupportDescription, technicalSupportPhone } = {} } = this.props;
    const isIconsContactHelpExp = split === 'iconsContactHelpExp';

    return (<FormattedMessage id={ fullHelpLinkId } >
      {(title) =>
        <Link
          data-eid='uxp.hyd.utility_bar.help.link.click'
          className={ this.namespace(this.classNames('help-link', {
            'help-icon-exp': isIconsContactHelpExp
          })) }
          urlArgs={ urlArgs }
          { ...urls.help }
          target='_blank'
          title={ title }
          ariaLabel={ technicalSupportDescription + ' ' + technicalSupportPhone }>
          <span className={ this.namespace('help-icon') }>
            <Help width={ 28 } height={ 28 }/>
          </span>
          <span className={ this.namespace('help-link-text') }>
            <FormattedMessage id={ fullHelpLinkId }/>
          </span>
        </Link>
      }
    </FormattedMessage>
    );
  }

  renderFeaturedNav() {
    const {
      urlArgs,
      domainsExp,
      featuredNav,
      messages,
      market,
      scaleFeaturedNav,
      eidPrefix
    } = this.props;

    if (scaleFeaturedNav) {
      return <HeaderNavigation
        ref={ r => { this.headerNav = r; } }
        messages={ messages }
        market={ market }
        eidPrefix={ eidPrefix }
        moreMenuEid={ `${eidPrefix}_more.menu.click` }
        items={ featuredNav }
        urlArgs={ urlArgs }
        scale />;
    }

    const navs = featuredNav.map(item => {
      if (!item.onlyMobile) {
        const { id } = item;
        const eid = eidBuilder({ key: id });
        return (<LinkBuilder { ...item }
          key={ id }
          eid={ eid }
          urlArgs={ urlArgs }
          classNames={ this.namespace('d-none', 'd-lg-flex') }
        />);
      }
    });

    return (
      <ul className={ this.namespace(
        this.classNames('featured-nav', {
          'domains-experiment': domainsExp
        }))
      }>
        { navs }
      </ul>
    );
  }

  /**
   * Render function for jsx specific to DeepSee
   * @param  {Object} logoProps  Props specific to logo
   * @param  {Object} cartProps  Props specific to cart
   * @param  {Object} childProps Values assembled from combining props and state
   * @returns {React.ReactElement} div for seechange elements
   */
  renderDeepSee(logoProps, cartProps, childProps) {
    const props = this.props;
    const components = this.components;
    const { split, messages, urls, urlArgs } = props;
    const { features } = childProps;
    const control = <React.Fragment>
      <span className={ this.namespace('product-flyout-btn-lines', 'no-treatment') }/>
      <div className={ this.namespace('product-flyout-btn-outline') }>
        <span className={ this.namespace('product-flyout-btn-lines') }/>
        <FormattedMessage id={ golf.productPanelControl } />
      </div>
      <div className={ this.namespace('product-flyout-title') }>
        <FormattedMessage id={ golf.productPanelControl } />
      </div>
    </React.Fragment>;
    const productPanelProps = {
      control,
      messages,
      urls,
      discoverNav: props.discoverNav,
      productNav: props.navigation,
      urlArgs,
      globalElements: false,
      controlWrapperClass: this.namespace(
        this.classNames({
          'product-panel-left': split === 'productpanela',
          'product-panel-c': split === 'productpanelc',
          'product-panel-e': split === 'productpanele'
        })
      )
    };
    const marketSelectorProps = assign({
      currentPage: props.currentPage,
      manifest: props.manifest,
      eidPrefix: 'uxp.hyd',
      urls,
      urlArgs,
      messages,
      currentMarket: props.market,
      numColumns: this.state.breakpoint === 'tablet' ? 2 : 4,
      marketData: props.marketData,
      isReseller: props.isReseller
    }, props.marketSelector);
    const eids = this.eids;
    const contactTrayProps = assign({
      contactTrayEnabled: features.contactTray,
      utilityChatEnabled: features.utilityChat,
      requestACallEnabled: features.requestACall,
      shopperId: props.customer.shopperId
    }, props);

    return (
      <div className={ this.namespace('flex-row', 'd-flex') }>
        <div className={ this.namespace('see-change-bar-left', 'd-flex') }>
          {
            components.logo &&
            <div className={ this.namespace(this.classNames('topnav-logo-wrap', {
              'product-panel-left': split === 'productpanela'
            })) }>
              <Logo { ...logoProps } />
            </div>
          }
          {
            components.marketSelector &&
            <div className={ this.namespace('market-selector-container') } >
              <MarketSelector { ...marketSelectorProps } />
            </div>
          }
          {
            components.productPanel && <ProductPanel { ...productPanelProps } />
          }
          { props.featuredNav && this.renderFeaturedNav() }
          { components.domainSearch && this.renderDomainSearch() }
        </div>
        <div className={ this.namespace('see-change-bar-right', 'justify-content-end') }>
          {
            components.contactTray &&
              <ContactTray { ...contactTrayProps } />
          }
          {
            components.blog && props.urls.blog &&
              <Link
                className={ this.namespace('blog-link') }
                urlArgs={ props.urlArgs }
                { ...props.urls.blog }
                data-eid={ eids.blogLink }>
                <FormattedMessage id={ golf.blogLink } />
              </Link>
          }
          { this.renderGlobalElements(cartProps, childProps) }
        </div>
      </div>
    );
  }

  /**
   * Render function for jsx in all non-deepsee instances of utility-bar
   * @param  {Object} logoProps  Props specific to logo
   * @param  {Object} cartProps  Props specific to cart
   * @param  {Object} childProps Values assembled from combining props and state
   * @returns {React.ReactElement} div for normal rendering of elements
   */
  renderOldBrand(logoProps, cartProps, childProps) {
    const props = this.props;
    const components = this.components;
    const eids = this.eids;
    const { features } = childProps;
    const marketSelectorProps = assign({
      currentPage: props.currentPage,
      manifest: props.manifest,
      eidPrefix: 'uxp.hyd',
      urls: props.urls,
      urlArgs: props.urlArgs,
      messages: props.messages,
      currentMarket: props.market,
      numColumns: this.state.breakpoint === 'tablet' ? 2 : 4,
      marketData: props.marketData,
      isReseller: props.isReseller
    }, props.marketSelector);
    const contactTrayProps = assign({
      contactTrayEnabled: features.contactTray,
      utilityChatEnabled: features.utilityChat,
      shopperId: props.customer.shopperId
    }, props);

    const utilSeparator = <div className={ this.namespace('util-separator') } />;

    return (
      <div className={ this.namespace('row') }>
        <div className={ this.namespace('flex-row') }>
          <div className={ this.namespace('utility-left-nav') }>
            {
              components.logo &&
              <div className={ this.namespace('topnav-logo-wrap') }>
                <Logo { ...logoProps } />
              </div>
            }
            {
              components.marketSelector && !props.isReseller &&
              <div className={ this.namespace('market-selector-container') } >
                <MarketSelector { ...marketSelectorProps } />
              </div>
            }
          </div>
          <div className={ this.namespace('utility-right-nav') }>
            {
              components.utilitySeparator && props.isPaymentHeader && utilSeparator
            }
            {
              components.contactTray &&
                <ContactTray { ...contactTrayProps } />
            }
            {
              components.marketSelector && props.isReseller && <MarketSelector { ...marketSelectorProps } />
            }
            {
              // if there is no props.urls.blog, then blog is blacklisted
              components.blog && props.urls.blog &&
                <Link
                  className={ this.namespace('blog-link') }
                  urlArgs={ props.urlArgs }
                  { ...props.urls.blog }
                  data-eid={ eids.blogLink }>
                  <FormattedMessage id={ golf.blogLink } />
                </Link>
            }
            {
              components.help && this.renderHelpCenter()
            }
            {
              components.utilitySeparator &&
              props.loggedIn && utilSeparator
            }
            {
              components.notifications && this.props.loggedIn &&
              this.renderNotifications()
            }
            {
              components.utilitySeparator && !props.isPaymentHeader && utilSeparator
            }
            {
              components.utilitySeparator && props.isPaymentHeader &&
              !props.loggedIn && utilSeparator
            }
            {
              components.accountTray && <AccountTray { ...childProps } oldBrand={ true }/>
            }
            {
              components.cart && <Cart { ...cartProps } />
            }
          </div>
        </div>
      </div>
    );
  }

  /**
 * Renders utility-bar
 * @returns {React.ReactElement} the whole utility-bar
 */
  render() {
    const props = this.props;
    const { logos, isReseller, resellerStorefrontLogoSize } = props;
    const components = this.components;
    const childProps = this.getChildProps();
    const marketInfo = props.marketData[props.market];
    const isPrivateLabel = props.privateLabelId !== 1;
    this.countryName = marketInfo ? marketInfo.countryName : 'United States';

    // Only apply logo size class name on reseller storefront for private
    // labels with a desktop logo and a size setting other than 'small'.
    const plLogoSize = isReseller && isPrivateLabel && logos && logos.desktop &&
      resellerStorefrontLogoSize && resellerStorefrontLogoSize !== 'small' &&
      `pl-${resellerStorefrontLogoSize}-logo`;

    const logoProps = assign({
      'className': this.namespace('topnav-logo', props.tempLogo && 'temp-logo-anchor', plLogoSize),
      'showCountryNameOnLogo': !this.isMobile && this.state.breakpoint !== 'tablet',
      'country': this.countryName,
      'href': props.urls.home.href,
      'logos': props.logos,
      'market': props.market,
      'messages': props.messages,
      'privateLabelId': props.privateLabelId,
      'privateLabelName': props.privateLabelName,
      'urlArgs': props.urlArgs,
      'tempLogo': props.tempLogo,
      'urls': props.urls,
      'data-eid': 'uxp.hyd.utility_bar.logo.link.click'
    }, props.logo);

    const cartProps = assign({ }, props.cart);

    if (typeof this.props.items === 'number') {
      cartProps.items = this.props.items;
    }

    // isReseller comes from sales-header
    // isPrivateLabel must be calculated for utility-header
    const utilityBarClasses = this.namespace(this.classNames({
      'utility-bar': this.oldBrand,
      'single-use-header': components.logo && !props.isPaymentHeader && this.oldBrand,
      'payment-header': props.isPaymentHeader,
      'pl': isPrivateLabel,
      'pl-no-logo': isPrivateLabel && (!logos || (!(this.isMobile ? logos.mobile : logos.desktop))),
      'reseller': isReseller,
      'language-header': props.preset === 'language-header',
      'brand-header': props.preset === 'brand-header',
      'logged-in': props.loggedIn,
      'see-change-bar': !this.oldBrand
    }));

    return (
      <IntlProvider locale={ props.market } messages={ props.messages }>
        <div>
          {
            components.accountDelegation &&
              <AccountDelegation
                market={ props.market }
                messages={ props.messages }
                action={ props.urls.sso.exitDelegation.href }
                restoreCookie={ props.urls.sso.restoreCookie.href }
                customer={ props.customer } />
          }
          <div className={ utilityBarClasses }>
            <div className={ this.namespace(this.oldBrand ? 'container' : 'container-fluid') }>
              { this.oldBrand ?
                this.renderOldBrand(logoProps, cartProps, childProps) :
                this.renderDeepSee(logoProps, cartProps, childProps) }
            </div>
          </div>
        </div>
      </IntlProvider>
    );
  }
}

UtilityBar.propTypes = {
  ...NamespaceShape,
  components: PropTypes.shape({
    marketSelector: PropTypes.bool,
    contactTray: PropTypes.bool,
    help: PropTypes.bool,
    notifications: PropTypes.bool,
    accountTray: PropTypes.bool,
    cart: PropTypes.bool,
    blog: PropTypes.bool,
    logo: PropTypes.bool
  }),
  bottomNav: PropTypes.array,
  CartComponent: PropTypes.elementType,
  contactTray: PropTypes.object,
  currentHref: PropTypes.string,
  customer: PropTypes.object.isRequired,
  discoverNav: PropTypes.array,
  eidPrefix: PropTypes.string,
  features: PropTypes.object.isRequired,
  loggedIn: PropTypes.bool,
  manifest: PropTypes.string,
  market: PropTypes.string.isRequired,
  marketSelector: PropTypes.object,
  marketData: PropTypes.object,
  messages: PropTypes.object.isRequired,
  navigation: PropTypes.array,
  privateLabelId: PropTypes.number,
  privateLabelName: PropTypes.string,
  showAcccountTrayComponent: PropTypes.bool,
  supportContacts: PropTypes.object,
  tempLogo: PropTypes.object,
  urlArgs: PropTypes.object,
  urls: PropTypes.object.isRequired,
  utilityBarNav: PropTypes.arrayOf(PropTypes.object),
  isPaymentHeader: PropTypes.bool,
  isReseller: PropTypes.bool,
  fullHelpLinkId: PropTypes.string,
  resellerStorefrontLogoSize: PropTypes.string,
  domainsExp: PropTypes.bool,
  scaleFeaturedNav: PropTypes.bool
};

UtilityBar.defaultProps = {
  utilityBarNav: [],
  components: defaultComponents,
  contactTray: { compact: true },
  features: { },
  privateLabelId: 1,
  privateLabelName: '',
  isPaymentHeader: false,
  isReseller: false,
  marketData: {},
  fullHelpLinkId: golf.helpLink,
  scaleFeaturedNav: false,
  eidPrefix: 'uxp.hyd.featured_nav'
};

export default withNamespace(UtilityBar);
